import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {FullCalendarComponent} from '@fullcalendar/angular';
import {CalendarOptions, EventClickArg, EventDropArg, EventInput} from '@fullcalendar/core';
import {DateClickArg, Draggable} from '@fullcalendar/interaction';
import {DataService} from '../../../Services/data.service';
import {ExternalEvent} from '../shared/event.model';

// component
import { EventComponent } from '../event/event.component';

// data
import { calendarEvents, externalEvents } from '../shared/data';
import {HttpErrorResponse} from '@angular/common/http';
import {NotificationService} from '../../../Services/notification.service';
import {AuthService} from '../../../Services/auth.service';

// calendar plugins
import {SendEmailModalComponent} from '../../common/send-email-modal/send-email-modal.component';
import bootstrapPlugin from '@fullcalendar/bootstrap';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import {Title} from '@angular/platform-browser';
import {SysUserModel} from '../../../DataModels/sys-user-model';
import {CalendarDto, FullCalendarEventDto} from '../../../DataModels/calendar-event-model';
import {firstValueFrom} from 'rxjs';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';
import {Modal, Popover} from 'bootstrap';
import {HelperService} from '../../../Services/helper/helper.service';
import {EmailModel} from '../../../DataModels/email-model';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  standalone: false
})
export class CalendarComponent implements OnInit, AfterViewInit {

  calendarOptions: CalendarOptions = {};
  selectedDay: any = {};
  isEditable = false;
  isDragging = false;
  calendarEventsData: EventInput[] = [];
  event: EventInput = {};
  externalEventsData: ExternalEvent[] = [];
  user: SysUserModel;
  users: SysUserModel[] = [];
  reportDataToSendInEmail: EmailModel;
  selectedCategory: string = 'All';
  categories: string[] = ['All'];
  calendars: CalendarDto[] = [];
  selectedCalendarId: string | null | 'All' = 'All';

  @ViewChild('calendar')
  calendarComponent!: FullCalendarComponent;

  @ViewChild('eventModal', { static: true }) eventModal!: EventComponent;
  @ViewChild(SendEmailModalComponent, {read: ElementRef}) private emailModalHost: ElementRef;

  constructor(
    private tableDataService: DataService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private titleService: Title,
    private confirmDialogService: ConfirmDialogService,
    private helperService: HelperService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // this.pageTitle = [{ label: 'Apps', path: '/' }, { label: 'Calendar', path: '/', active: true }];
    this.titleService.setTitle($localize`:Schedule@@schedule:Schedule`);

    // full calendar config
    this.calendarOptions = {
      themeSystem: 'bootstrap',
      bootstrapFontAwesome: false,
      buttonText: {
        today: 'Today',
        month: 'Month',
        week: 'Week',
        day: 'Day',
        list: 'List',
        prev: 'Prev',
        next: 'Next'
      },
        plugins: [
          dayGridPlugin,
          interactionPlugin,
          bootstrapPlugin,
          timeGridPlugin,
          listPlugin
        ],
      initialView: 'dayGridMonth',
      timeZone: 'local',
      handleWindowResize: true,
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'
      },
      editable: true,
      droppable: true, // this allows things to be dropped onto the calendar
      selectable: true,
      select: this.handleSelect.bind(this),
      dateClick: this.handleDateClick.bind(this),
      eventClick: this.handleEventClick.bind(this),
      eventMouseEnter: this.handleEventMouseEnter.bind(this),
      eventMouseLeave: this.handleEventMouseLeave.bind(this),
      drop: this.onDrop.bind(this),
      eventDrop: this.onEventDrop.bind(this),
      eventDragStart: this.handleEventDragStart.bind(this),
      eventDragStop: this.handleEventDragStop.bind(this),
      selectAllow: (selectInfo) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return selectInfo.start >= today;
      },
      eventAllow: (dropInfo) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dropDate = new Date(dropInfo.start);
        dropDate.setHours(0, 0, 0, 0);
        return dropDate >= today;
      },
      eventDidMount: (info) => {
        const isPrivate = info.event.extendedProps?.['isPrivate'] ?? (info.event as any)['isPrivate'];
        const isOrganizer = info.event.extendedProps?.IsOrganizer;
        if (isPrivate && isOrganizer) {
          const lockEl = document.createElement('span');
          lockEl.innerHTML = ' <i class="bi bi-lock-fill" title="Private event"></i>';
          lockEl.style.marginLeft = '4px';
          const titleEl = info.el.querySelector('.fc-event-title');
          if (titleEl) {
            titleEl.appendChild(lockEl);
          }
        }
      }
    };

    this.getSchedulerData().then();
    this.loadUsers();
    this.loadCalendars();
  }

  ngAfterViewInit(): void {
    new Draggable(document.getElementById('external-events')!, {
      itemSelector: '.external-event',
      eventData: (eventEl) => {
        // Trigger cleaning up popovers when starting to drag an external event
        this.handleExternalDragStart(eventEl);
        return {
          title: eventEl.innerText,
          duration: '01:00'
        };
      }
    });
  }

  async getSchedulerData(): Promise<void> {
    try {
      this.user = await firstValueFrom(this.authService.userInfoSubject);
    } catch (e) {
      console.error('Error getting user info:', e);
    }

    // 1. Continue to load fake data for demo purposes (as requested)
    this.calendarEventsData = [...calendarEvents];
    this.externalEventsData = [...externalEvents];

    this.extractCategories();

    // Update calendar options with initial fake data
    this.refreshCalendarView();

    if (!this.user?.userId) {
      console.warn('User ID not available for fetching Calendar Events');
      return;
    }

    // 2. Fetch real data from the new API
    this.loadCalendarEvents();
  }

  loadCalendarEvents(): void {
    if (!this.user?.userId) {
      return;
    }
    this.tableDataService.getData('Calendar', '?currentUserId=' + this.user.userId).subscribe({
      next: (result: any[]) => {
        // console.log('API Response for calendar data:', result);
        if (result && result.length > 0) {
          const apiEvents: EventInput[] = result.map(e => {
            const rawAttendees = e.Attendees || e.ExtendedProps?.Attendees || [];

            // Map attendee Names if we have user list available
            const mappedAttendees = rawAttendees.map((a: any) => {
              const userId = a.UserId;
              const foundUser = this.users.find(u => u.id === userId);
              return {
                UserId: userId,
                Status: a.Status || 'Pending',
                Name: a.Name || foundUser?.name
              };
            });

            // Helper to clean ISO8601 strings for FullCalendar
            // If AllDay is true, it should be YYYY-MM-DD (no time part)
            const getEventDate = (date: any, isAllDay: boolean) => {
              if (!date) {
                return undefined;
              }
              if (typeof date === 'string') {
                if (isAllDay && date.includes('T')) {
                  return date.split('T')[0];
                }
                return date;
              }
              if (date instanceof Date) {
                const tzOffset = date.getTimezoneOffset() * 60000;
                const localISO = new Date(date.getTime() - tzOffset).toISOString();
                return isAllDay ? localISO.split('T')[0] : localISO.slice(0, 19);
              }
              return date;
            };

            const eventStart = getEventDate(e.Start, e.AllDay);
            const eventEnd = getEventDate(e.End, e.AllDay);

            return {
              Id: e.Id,
              Title: e.Title,
              Start: eventStart,
              End: eventEnd,
              StartTime: e.StartTime,
              EndTime: e.EndTime,
              AllDay: e.AllDay,
              DaysOfWeek: e.DaysOfWeek,
              StartRecur: e.StartRecur,
              EndRecur: e.EndRecur,
              id: e.Id,
              title: e.Title,
              start: eventStart,
              end: eventEnd,
              color: e.ExtendedProps?.Priority === 'danger' ? '#fa5c7c' : (e.ExtendedProps?.Priority === 'success' ? '#0acf97' : (e.ExtendedProps?.Priority === 'warning' ? '#ffbc00' : '#727cf5')),
              classNames: e.ClassNames || (e.ExtendedProps?.IsOrganizer ? 'bg-primary-subtle' : 'bg-info-subtle'),
              // FullCalendar uses duration for recurring events to determine their length
              duration: this.calculateDuration(e.StartTime, e.EndTime) || this.calculateDuration(e.Start, e.End),
              calendarId: e.CalendarId ?? e.calendarId ?? null,
              isPrivate: e.IsPrivate ?? e.isPrivate ?? false,
              extendedProps: {
                ...e.ExtendedProps,
                IsOrganizer: e.ExtendedProps?.IsOrganizer !== undefined ? e.ExtendedProps.IsOrganizer : true,
                OrganizerId: e.ExtendedProps?.OrganizerId || this.user.userId || this.user.id,
                Attendees: mappedAttendees,
                calendarId: e.CalendarId ?? e.calendarId ?? null,
                isPrivate: e.IsPrivate ?? e.isPrivate ?? false,
                // Keep raw dates for modal use to avoid FullCalendar's Date conversion issues
                RawStart: e.Start,
                RawEnd: e.End,
                RawStartRecur: e.StartRecur,
                RawEndRecur: e.EndRecur
              }
            };
          });

          // Merge API events into calendarEventsData
          this.calendarEventsData = [...this.calendarEventsData, ...apiEvents];

          this.extractCategories();

          // Update calendar options to reflect new events
          this.refreshCalendarView();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.notificationService.error($localize`:Error loading Calendar Events @@errorloadingCalendarEvents:Error loading Calendar Events`);
        console.error('API Error:', error);
      }
    });
  }

  /**
   * Calculates the duration between two times or dates
   * @param start start time/date
   * @param end end time/date
   * @private
   */
  private calculateDuration(start: any, end: any): string | null {
    if (!start || !end) {
      return null;
    }

    try {
      let startDate: Date;
      let endDate: Date;

      if (typeof start === 'string' && start.includes(':') && !start.includes('T') && !start.includes('-')) {
        // Handle "HH:mm:ss" time strings
        startDate = new Date(`1970-01-01T${start}`);
        endDate = new Date(`1970-01-01T${end}`);
      } else {
        startDate = new Date(start);
        endDate = new Date(end);
      }

      const diffMs = endDate.getTime() - startDate.getTime();
      if (isNaN(diffMs) || diffMs < 0) {
        return null;
      }

      const hours = Math.floor(diffMs / 3600000);
      const minutes = Math.floor((diffMs % 3600000) / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);

      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } catch (e) {
      return null;
    }
  }

  /**
   * Opens event modal
   * @param title title of modal
   * @param data data to be used in modal
   */
  openEventModal(title: string = '', data: any = {}) {
    // Force direct assignment to ensure child component has users before opening
    if (this.eventModal) {
      this.eventModal.users = [...this.users];
      if (this.users.length > 0) {
        (this.eventModal as any).usersLoaded = true;
      }
      this.eventModal.openModal(title, data);
    }
  }

  /**
   * Creates new event for today
   */
  createNewEvent(): void {
    const start = new Date();
    start.setHours(12, 0, 0, 0);
    const end = new Date(start);
    end.setHours(13, 0, 0, 0);

    this.event = {
      Id: '',
      Title: '',
      Category: 'General',
      Priority: 'danger',
      Start: start,
      End: end,
      AllDay: false,
      ExtendedProps: {
        Priority: 'danger',
        Category: 'General'
      },
      calendarId: this.selectedCalendarId === 'All' ? null : this.selectedCalendarId
    };
    this.isEditable = false;
    this.openEventModal('Add New Event', this.event);
  }


  /**
   * adds external events by Drag n Drop
   * @param event dropped event
   */
  onDrop(event: any): void {
    // Force cleanup any remaining popovers on drop
    this.forceClosePopovers();

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dropDate = new Date(event.date);

    if (dropDate < today) {
      this.notificationService.error('Cannot add events in the past');
      this.isDragging = false;
      return;
    }

    const draggedEl = event.draggedEl;
    const priority = draggedEl.getAttribute('data-type') || 'primary';

    const start = new Date(event.date);
    start.setHours(12, 0, 0, 0);
    const end = new Date(start);
    end.setHours(13, 0, 0, 0);

    this.event = {
      Id: '',
      Title: draggedEl.innerText,
      Start: start,
      End: end,
      AllDay: false,
      Priority: priority,
      ExtendedProps: {
        Priority: priority,
        Category: 'General'
      },
      Category: 'General',
      calendarId: this.selectedCalendarId === 'All' ? null : this.selectedCalendarId
    };
    this.isEditable = false;
    this.openEventModal('Add New Event', this.event);

    // Delay setting isDragging to false to prevent immediate mouseEnter from opening popover
    setTimeout(() => {
      this.isDragging = false;
    }, 100);
  }

  /**
   * on event drop between calendar
   */
  onEventDrop(arg: EventDropArg): void {
    // Force cleanup any remaining popovers on drop
    this.forceClosePopovers();

    const event = arg.event;
    const oldEvent = arg.oldEvent;

    // Protection for past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (event.start && event.start < today) {
      this.notificationService.error('Cannot move events to the past');
      arg.revert();
      this.isDragging = false;
      return;
    }

    // Use original times if they exist, otherwise use what's in the event
    // arg.delta contains the movement information
    // If it was AllDay, we might want to keep it AllDay or convert?
    // User says "change only the dates - times stay as they were"

    let newStart = event.start;
    let newEnd = event.end;

    if (oldEvent.start && event.start) {
      // Create new date objects to avoid mutating
      newStart = new Date(event.start);
      // Restore original time
      newStart.setHours(oldEvent.start.getHours());
      newStart.setMinutes(oldEvent.start.getMinutes());
      newStart.setSeconds(oldEvent.start.getSeconds());
      newStart.setMilliseconds(oldEvent.start.getMilliseconds());

      if (oldEvent.end) {
        // Calculate original duration in milliseconds
        const durationMs = oldEvent.end.getTime() - oldEvent.start.getTime();
        newEnd = new Date(newStart.getTime() + durationMs);
      } else {
        // If there was no end date, maybe it was a same day event or just a point in time
        // The requirement says "if it was same day then End is 16th also" (for a drag from 14 to 16)
        // This implies duration is preserved. If start and end were same, duration is 0.
        newEnd = new Date(newStart);
      }
    }

    this.event = {
      Id: event.id,
      Title: event.title,
      Start: newStart,
      End: newEnd,
      AllDay: event.allDay,
      ClassNames: event.classNames,
      ExtendedProps: event.extendedProps,
      // Map properties from extendedProps to top-level for handleEventSave
      Priority: event.extendedProps?.Priority || (event.classNames && event.classNames[0] ? event.classNames[0].replace('bg-', '').replace('-subtle', '') : 'primary'),
      Category: event.extendedProps?.Category || 'General',
      Description: event.extendedProps?.Description,
      IsOrganizer: event.extendedProps?.IsOrganizer !== undefined ? event.extendedProps.IsOrganizer : true,
      MyStatus: event.extendedProps?.MyStatus,
      Attendees: event.extendedProps?.Attendees || [],
      calendarId: (event as any).calendarId ?? event.extendedProps?.calendarId ?? null,
      isPrivate: (event as any).isPrivate ?? event.extendedProps?.isPrivate ?? false
    };

    const isDemo = !isNaN(Number(event.id)) && Number(event.id) < 100;
    if (!isDemo) {
      // For existing API events, we save directly on drop
      this.isEditable = true;
      this.handleEventSave(this.event);
    } else {
      // For demo events, open the modal for confirmation
      this.isEditable = true;
      this.openEventModal('Edit Event', this.event);
    }

    // Delay setting isDragging to false to prevent immediate mouseEnter from opening popover
    setTimeout(() => {
      this.isDragging = false;
    }, 100);
  }

  /**
   * Handle event drag start to hide tooltip
   */
  handleEventDragStart(arg: any): void {
    this.isDragging = true;
    this.handleEventMouseLeave(arg);

    // Also close any other open popovers to be sure
    this.forceClosePopovers();
  }

  /**
   * Handle event drag stop
   */
  handleEventDragStop(arg: any): void {
    // Delay setting isDragging to false to prevent immediate mouseEnter from opening popover
    setTimeout(() => {
      this.isDragging = false;
    }, 100);
    this.forceClosePopovers();
  }


  /**
   * Handle drag start for external events
   */
  handleExternalDragStart(event: any): void {
    this.isDragging = true;
    // When starting to drag an external event, clear any existing popovers
    this.forceClosePopovers();
  }

  /**
   * Forcefully close all popovers from the DOM
   */
  private forceClosePopovers(): void {
    const popovers = document.querySelectorAll('.popover');
    popovers.forEach(popoverEl => {
      popoverEl.remove();
    });
  }


  /**
   * Handling date click on calendar
   * @param arg DateClickArg
   */
  handleDateClick(arg: DateClickArg): void {
    // If selectable is true, handleSelect will also fire.
    // We only open the modal in handleSelect to avoid double modals.
  }

  /**
   * Handling select on calendar (drag to select range)
   * @param arg any
   */
  handleSelect(arg: any): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (new Date(arg.start) < today) {
      return;
    }

    this.selectedDay = arg;

    const start = new Date(arg.start);
    start.setHours(12, 0, 0, 0);
    const end = new Date(start);
    end.setHours(13, 0, 0, 0);

    this.event = {
      Id: '',
      Title: '',
      Category: 'General',
      Priority: 'danger',
      Start: start,
      End: end,
      AllDay: false, // Do not default to all day
      ExtendedProps: {
        Priority: 'danger',
        Category: 'General'
      },
      calendarId: this.selectedCalendarId === 'All' ? null : this.selectedCalendarId
    };
    this.isEditable = false;
    this.openEventModal('Add New Event', this.event);
  }


  /**
   * Handling click on event on calendar
   * @param arg EventClickArg
   */
  handleEventClick(arg: EventClickArg): void {
    const event = arg.event;
    // Remove tooltip if any when opening the modal
    this.handleEventMouseLeave(arg);

    this.event = {
      Id: event.id,
      Title: event.title,
      Start: event.extendedProps?.['RawStart'] || event.start,
      End: event.extendedProps?.['RawEnd'] || event.end,
      StartTime: event.extendedProps?.StartTime || (event as any)._def?.recurringDef?.typeData?.startTime,
      EndTime: event.extendedProps?.EndTime || (event as any)._def?.recurringDef?.typeData?.endTime,
      AllDay: event.allDay,
      ClassNames: event.classNames,
      ExtendedProps: {...event.extendedProps},
      DaysOfWeek: event.extendedProps?.DaysOfWeek || (event as any)._def?.recurringDef?.typeData?.daysOfWeek,
      StartRecur: event.extendedProps?.['RawStartRecur'] || event.extendedProps?.StartRecur || (event as any)._def?.recurringDef?.typeData?.startRecur,
      EndRecur: event.extendedProps?.['RawEndRecur'] || event.extendedProps?.EndRecur || (event as any)._def?.recurringDef?.typeData?.endRecur,
      Attendees: event.extendedProps?.Attendees || [],
      calendarId: event.extendedProps?.calendarId ?? null,
      isPrivate: event.extendedProps?.isPrivate ?? false
    };

    this.isEditable = true;
    this.openEventModal('Edit Event', this.event);
  }

  /**
   * Handle mouse enter event to show popover
   * @param arg any
   */
  handleEventMouseEnter(arg: any): void {
    if (this.isDragging) {
      return;
    }
    // Check if the modal is currently open to avoid stray popovers
    if (document.querySelector('.modal.show')) {
      return;
    }
    if (arg.event.title) {
      const popover = Popover.getOrCreateInstance(arg.el, {
        title: arg.event.title,
        content: arg.event.extendedProps?.Description || '',
        placement: 'top',
        trigger: 'manual',
        container: 'body'
      });
      popover.show();
    }
  }

  /**
   * Handle mouse leave event to hide popover
   * @param arg any
   */
  handleEventMouseLeave(arg: any): void {
    const popover = Popover.getInstance(arg.el);
    if (popover) {
      popover.dispose();
    }
  }

  /**
   * Handle the event save
   * @param newEvent new event
   */
  handleEventSave(newEvent: any): void {
    // console.log('handleEventSave received event:', JSON.stringify(newEvent, null, 2));

    const attendeesList = (Array.isArray(newEvent.Attendees) ? newEvent.Attendees : []).map((a: any) => ({
      UserId: a.UserId,
      Status: a.Status || 'Pending'
    }));

    if (this.isEditable) {
      const isDemo = !isNaN(Number(newEvent.Id)) && Number(newEvent.Id) < 100;
      if (!isDemo) {
        const updateObj: FullCalendarEventDto = {
          Id: newEvent.Id,
          Title: newEvent.Title,
          ClassNames: `bg-${newEvent.Priority}-subtle`,
          AllDay: newEvent.AllDay,
          calendarId: newEvent.calendarId ?? null,
          isPrivate: newEvent.isPrivate ?? false,
          Start: newEvent.Start,
          End: newEvent.End,
          StartTime: newEvent.StartTime,
          EndTime: newEvent.EndTime,
          DaysOfWeek: newEvent.DaysOfWeek,
          StartRecur: newEvent.StartRecur,
          EndRecur: newEvent.EndRecur,
          Attendees: attendeesList,
          OrganizerId: this.user.userId || this.user.id,
          ExtendedProps: {
            Description: newEvent.Description,
            IsOrganizer: newEvent.IsOrganizer !== undefined ? newEvent.IsOrganizer : true,
            MyStatus: newEvent.MyStatus || 'Organizer',
            OrganizerId: this.user.userId || this.user.id,
            Attendees: attendeesList,
            Category: newEvent.Category,
            Priority: newEvent.Priority
          }
        };
        // console.log('Calendar PUT Object:', updateObj);
        this.tableDataService.put('Calendar', String(newEvent.Id), updateObj).subscribe({
          next: () => {
            this.notificationService.success('Event updated');
            this.getSchedulerData().then();
          },
          error: () => this.notificationService.error('Failed to update event')
        });
      } else {
        // Update demo data locally
        const eventIndex = this.calendarEventsData.findIndex((event) => event.id === newEvent.Id);
        if (eventIndex !== -1) {
          this.calendarEventsData[eventIndex].title = newEvent.Title;
          this.calendarEventsData[eventIndex].classNames = newEvent.Category;
          this.calendarEventsData[eventIndex].start = newEvent.Start;
          this.calendarEventsData[eventIndex].end = newEvent.End;
          this.extractCategories();
          this.refreshCalendarView();
        }
      }
    }
    else {
      const saveObj: FullCalendarEventDto = {
        Id: '',
        Title: newEvent.Title,
        ClassNames: `bg-${newEvent.Priority}-subtle`,
        AllDay: newEvent.AllDay || false,
        calendarId: newEvent.calendarId ?? null,
        isPrivate: newEvent.isPrivate ?? false,
        Start: newEvent.Start,
        End: newEvent.End,
        StartTime: newEvent.StartTime,
        EndTime: newEvent.EndTime,
        DaysOfWeek: newEvent.DaysOfWeek,
        StartRecur: newEvent.StartRecur,
        EndRecur: newEvent.EndRecur,
        Attendees: attendeesList,
        OrganizerId: this.user.userId || this.user.id,
        ExtendedProps: {
          Description: newEvent.Description,
          IsOrganizer: true,
          MyStatus: 'Organizer',
          OrganizerId: this.user.userId || this.user.id,
          Attendees: attendeesList,
          Category: newEvent.Category,
          Priority: newEvent.Priority
        }
      };
      // console.log('Calendar POST Object:', JSON.stringify(saveObj, null, 2));
      this.tableDataService.post('Calendar', saveObj).subscribe({
        next: () => {
          this.notificationService.success('Event saved');
          this.getSchedulerData().then();
        },
        error: () => this.notificationService.error('Failed to save event')
      });
    }
    this.isEditable = false;
  }

  /**
   * Deletes calendar event
   * @param deleteEvent event to be deleted
   */
  handleEventDelete(deleteEvent: EventInput): void {

    const isDemo = !isNaN(Number(deleteEvent.Id)) && Number(deleteEvent.Id) < 100;

    this.confirmDialogService.confirmThis(
      [`Are you sure you want to delete the event '${deleteEvent.Title}'?`],
      () => {
        if (!isDemo) {
          this.tableDataService.deleteDataById('Calendar', deleteEvent.Id).subscribe({
            next: () => {
              this.notificationService.success('Event deleted');
              this.getSchedulerData().then();
            },
            error: () => this.notificationService.error('Failed to delete event')
          });
        } else {
          this.removeEventLocally(deleteEvent.Id);
        }
      },
      () => {
      }
    );
  }

  private removeEventLocally(id: string | number) {
    const modifiedEvents = [...this.calendarEventsData];
    const eventIndex = modifiedEvents.findIndex((event) => event.id === id);
    if (eventIndex !== -1) {
      modifiedEvents.splice(eventIndex, 1);
      this.calendarEventsData = modifiedEvents;
      this.extractCategories();
      this.refreshCalendarView();
    }
  }


  loadCalendars(): void {
    this.tableDataService.getData('Calendar/calendars').subscribe({
      next: (result: any) => {
        const raw = Array.isArray(result) ? result : (result?.Data ?? []);
        this.calendars = raw.map((c: any) => ({
          id: c.Id ?? c.id ?? null,
          name: c.Name ?? c.name ?? '',
          ownerId: c.OwnerId ?? c.ownerId ?? null,
          color: c.Color ?? c.color ?? null,
          isDefault: c.IsDefault ?? c.isDefault ?? false
        }));
      },
      error: () => {
      }
    });
  }

  onCalendarFilterChange(): void {
    this.refreshCalendarView();
  }

  loadUsers(): void {
    this.tableDataService.getData('sys_user').subscribe({
      next: (result: any) => {
        if (result && Array.isArray(result.Data)) {
          this.users = result.Data;
        } else if (Array.isArray(result)) {
          this.users = result;
        } else {
          this.users = [];
        }
      },
      error: (error: any) => {
        console.error('Error loading users:', error);
      }
    });
  }

  onCategoryChange(category: string): void {
    this.selectedCategory = category;
    this.refreshCalendarView();
  }

  onInviteResponded(): void {
    this.loadCalendarEvents();
  }

  get pendingInvitesReceived(): any[] {
    return this.calendarEventsData.filter(e => {
      const myStatus = e['extendedProps']?.MyStatus ?? e['extendedProps']?.myStatus;
      return myStatus === 'Pending';
    });
  }

  openSidebarEvent(ev: any): void {
    this.event = {
      Id: ev.id ?? ev.Id,
      Title: ev.title ?? ev.Title,
      Start: ev.extendedProps?.RawStart || ev.start,
      End: ev.extendedProps?.RawEnd || ev.end,
      AllDay: ev.allDay ?? ev.AllDay,
      ClassNames: ev.classNames ?? ev.ClassNames,
      ExtendedProps: {...(ev.extendedProps ?? {})},
      Attendees: ev.extendedProps?.Attendees || [],
      calendarId: ev.extendedProps?.calendarId ?? ev.calendarId ?? null,
      isPrivate: ev.extendedProps?.isPrivate ?? ev.isPrivate ?? false
    };
    this.isEditable = true;
    this.openEventModal('Edit Event', this.event);
  }

  get pendingInvitesSent(): any[] {
    return this.calendarEventsData.filter(e => {
      const isOrganizer = e['extendedProps']?.IsOrganizer ?? e['extendedProps']?.isOrganizer;
      const attendees: any[] = e['extendedProps']?.Attendees ?? e['extendedProps']?.attendees ?? [];
      return isOrganizer && attendees.some((a: any) => (a.Status ?? a.status) === 'Pending');
    });
  }

  private refreshCalendarView(): void {
    if (!this.calendarOptions) {
      return;
    }
    this.calendarOptions.events = this.getFilteredEvents();
  }

  private getFilteredEvents(): EventInput[] {
    const currentUserId = this.user?.userId || this.user?.id;
    let events = this.calendarEventsData.filter(e => {
      // Privacy filter: hide private events from non-organizers when viewing another user's calendar
      if (e['isPrivate'] && e['extendedProps']?.IsOrganizer === false) {
        return false;
      }
      return true;
    });
    if (this.selectedCalendarId !== 'All') {
      events = events.filter(e => {
        const cid = e['calendarId'] ?? e['extendedProps']?.calendarId ?? null;
        return cid === this.selectedCalendarId;
      });
    }
    if (this.selectedCategory !== 'All') {
      events = events.filter(e => this.getEventCategory(e) === this.selectedCategory);
    }
    return events;
  }

  private extractCategories(): void {
    const cats = new Set<string>();
    cats.add('All');
    this.calendarEventsData.forEach(e => {
      const category = this.getEventCategory(e);
      if (category && category !== 'default') {
        cats.add(category);
      }
    });
    this.categories = Array.from(cats).sort((a, b) => {
      if (a === 'All') {
        return -1;
      }
      if (b === 'All') {
        return 1;
      }
      return a.localeCompare(b);
    });
  }

  printReport() {
    try {
      const html = this.buildPrintHtml();
      this.helperService.printHtml(html);
    } catch (e) {
      console.error('Calendar print failed', e);
    }
  }

  sendReportEmail(): void {
    try {
      const html = this.buildPrintHtml();

      const dt = new Date();
      const ts = dt.toISOString().slice(0, 19).replace(/[:T]/g, '-');

      // Get view info for subject
      let viewTypeStr = '';
      let viewTitleStr = '';
      if (this.calendarComponent) {
        const calendarApi = this.calendarComponent.getApi();
        const view = calendarApi.view;
        viewTypeStr = view.type === 'dayGridMonth' ? 'Month View' :
          view.type === 'timeGridWeek' ? 'Week View' :
            view.type === 'timeGridDay' || view.type === 'listDay' ? 'Day View' : 'View';
        viewTitleStr = view.title || '';
      }

      const subject = viewTypeStr && viewTitleStr
        ? `Calendar Schedule - ${viewTypeStr} ${viewTitleStr}`
        : $localize`:Calendar Schedule@@calendarSchedule:Calendar Schedule`;

      // Bind for modal
      this.reportDataToSendInEmail = {
        toEmail: '',
        fromEmail: '',
        Subject: subject,
        body: $localize`:Please see the attached calendar schedule. @@seeAttachedCalendarSchedule:Please see the attached calendar schedule.`,
        header_id: null,
        entity_id: null,
        activity_message: 'Calendar schedule emailed',
        pdfHtml: html,
        FileName: `calendar_schedule_${ts}.pdf`,
        includeAttachment: true,
        attachment: null,
        batch_type_id: '',
        print_orientation: '2',
        convert_attachment_to: 'pdf',
      };

      // Open the email modal
      // Flush input bindings
      this.cdr.detectChanges();

      // Locate modal element via host
      let emailEl: HTMLElement | null = null;
      try {
        emailEl = (this.emailModalHost?.nativeElement as HTMLElement)?.querySelector?.('#sendEmailModal') || null;
      } catch {
        emailEl = null;
      }

      if (!emailEl) {
        emailEl = document.getElementById('sendEmailModal');
      }

      if (emailEl) {
        // If nested within any other modal, move to body (standard practice in this app)
        if (emailEl.parentElement && emailEl.closest('.modal')) {
          document.body.appendChild(emailEl);
        }
        const modalInstance = Modal.getOrCreateInstance(emailEl);
        modalInstance.show();
      } else {
        console.error('Email modal element not found');
        this.notificationService.error($localize`:Email modal not found @@emailModalNotFound:Email modal not found`);
      }

    } catch (e) {
      console.error(e);
      this.notificationService.warning($localize`:Unable to prepare email @@unableToPrepareEmail:Unable to prepare email`);
    }
  }

  private buildPrintHtml(): string {
    const apiEvents = this.getFilteredEvents();
    let reportTitle = 'Calendar Schedule';
    let filterDescription = '';
    let filteredEvents = [...apiEvents];
    let viewType = 'dayGridMonth';
    let viewTitle = '';
    let start: Date | null = null;
    let end: Date | null = null;

    // Get current view context from FullCalendar
    if (this.calendarComponent) {
      const calendarApi = this.calendarComponent.getApi();
      const view = calendarApi.view;
      viewType = view.type;
      viewTitle = view.title || '';
      start = view.activeStart;
      end = view.activeEnd;

      // Update title based on view type
      reportTitle = `${viewTitle} - Schedule`;
      filterDescription = `<p class="mb-3 text-muted">View: <strong>${view.type.charAt(0).toUpperCase() + view.type.slice(1)}</strong> | Period: <strong>${viewTitle}</strong></p>`;

      // Filter events to only show what's currently on screen
      filteredEvents = apiEvents.filter(event => {
        const eventStart = event.start ? new Date(event.start as any) : null;
        const eventEnd = event.end ? new Date(event.end as any) : eventStart;

        if (!eventStart) {
          return false;
        }

        // Check if event overlaps with the current view's range
        // (EventStart < ViewEnd) && (EventEnd > ViewStart)
        return eventStart < end && (eventEnd ? eventEnd > start : true);
      });
    }

    // Sort filtered events by start date for the list view
    const sortedEvents = [...filteredEvents].sort((a: any, b: any) => {
      const dateA = new Date(a.start).getTime();
      const dateB = new Date(b.start).getTime();
      return dateA - dateB;
    });

    // Generate the Visual Calendar Grid
    let gridHtml = '';
    if (start && end) {
      if (viewType === 'dayGridMonth') {
        gridHtml = this.buildMonthGrid(start, end, filteredEvents);
      } else if (viewType === 'timeGridWeek') {
        gridHtml = this.buildWeekGrid(start, end, filteredEvents);
      } else if (viewType === 'timeGridDay' || viewType === 'listDay') {
        gridHtml = this.buildDayGrid(start, end, filteredEvents);
      }
    }

    let tableRows = '';
    for (const event of sortedEvents) {
      const startStr = event.start ? new Date(event.start as any).toLocaleString() : '';
      const endStr = event.end ? new Date(event.end as any).toLocaleString() : '';

      const category = this.getEventCategory(event);
      const description = this.getEventDescription(event);

      tableRows += `
        <tr>
          <td style="font-weight: 600; color: #2c3e50;">${this.escapeHtml(event.title || '')}</td>
          <td>${startStr}</td>
          <td>${endStr}</td>
          <td><span class="badge-cat bg-${category}">${this.escapeHtml(String(category))}</span></td>
          <td style="color: #666; font-style: italic;">${this.escapeHtml(String(description))}</td>
        </tr>
      `;
    }

    const content = `
      <style>
        .calendar-report { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #333; }
        .calendar-grid { width: 100%; border-collapse: separate; border-spacing: 0; table-layout: fixed; margin-bottom: 30px; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
        .calendar-grid th { background: #f8f9fa; border-bottom: 1px solid #e0e0e0; border-right: 1px solid #e0e0e0; padding: 10px; font-size: 12px; font-weight: 600; text-transform: uppercase; color: #666; text-align: center; width: 14.28%; }
        .calendar-grid th:last-child { border-right: none; }
        .calendar-grid td { border-bottom: 1px solid #f0f0f0; border-right: 1px solid #f0f0f0; height: 110px; vertical-align: top; padding: 6px; font-size: 11px; width: 14.28%; transition: background 0.2s; }
        .calendar-grid td:last-child { border-right: none; }
        .calendar-grid tr:last-child td { border-bottom: none; }
        .calendar-grid .day-number { font-weight: 700; margin-bottom: 6px; display: inline-block; color: #444; background: #f0f0f0; padding: 2px 6px; border-radius: 4px; font-size: 10px; }
        .calendar-grid .other-month { background-color: #fafafa; opacity: 0.6; }
        .calendar-grid .event-item {
          display: block;
          padding: 3px 6px;
          margin-bottom: 3px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 500;
          color: white;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          border-left: 3px solid rgba(0,0,0,0.1);
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }

        /* Modern Soft Palette */
        .bg-primary { background-color: #4e73df !important; }
        .bg-success { background-color: #1cc88a !important; }
        .bg-danger { background-color: #e74a3b !important; }
        .bg-warning { background-color: #f6c23e !important; color: #fff !important; }
        .bg-info { background-color: #36b9cc !important; }
        .bg-secondary { background-color: #858796 !important; }
        .bg-dark { background-color: #5a5c69 !important; }
        .bg-default { background-color: #4e73df !important; }

        .section-header {
          display: flex;
          align-items: center;
          background: #fff;
          padding: 12px 0;
          margin: 25px 0 15px;
          border-bottom: 2px solid #4e73df;
          color: #4e73df;
          font-weight: 700;
          font-size: 16px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .report-table { width: 100%; border-collapse: collapse; margin-top: 10px; border-radius: 8px; overflow: hidden; }
        .report-table th { background: #f8f9fa; color: #666; font-weight: 600; text-align: left; padding: 12px 15px; font-size: 13px; border-bottom: 2px solid #e0e0e0; }
        .report-table td { padding: 12px 15px; font-size: 13px; border-bottom: 1px solid #f0f0f0; color: #444; }
        .report-table tr:last-child td { border-bottom: none; }
        .report-table tr:hover { background-color: #fcfcfc; }
        .badge-cat { padding: 4px 8px; border-radius: 50px; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #fff; display: inline-block; }
      </style>

      <div class="calendar-report">
        ${filterDescription}

        ${gridHtml ? `<div class="section-header"><span style="margin-right: 8px;">📅</span> Visual Calendar View</div>${gridHtml}` : ''}

        <div class="section-header"><span style="margin-right: 8px;">📝</span> Event Details List</div>
        <table class="report-table">
          <thead>
            <tr>
              <th>Event Title</th>
              <th>Start Time</th>
              <th>End Time</th>
              <th>Category</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows || '<tr><td colspan="5" class="text-center" style="padding: 30px; color: #999;">No events scheduled for this period.</td></tr>'}
          </tbody>
        </table>
      </div>
    `;

    return this.helperService.buildPdfHtml(content, reportTitle);
  }

  private buildMonthGrid(start: Date, end: Date, events: EventInput[]): string {
    let html = '<table class="calendar-grid"><thead><tr>';
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    days.forEach(day => html += `<th>${day}</th>`);
    html += '</tr></thead><tbody>';

    const curr = new Date(start);
    // Align to Sunday of the first week
    curr.setDate(curr.getDate() - curr.getDay());

    // We usually show 6 weeks (42 days) for a consistent month view
    for (let w = 0; w < 6; w++) {
      html += '<tr>';
      for (let d = 0; d < 7; d++) {
        const isOtherMonth = curr.getMonth() !== start.getMonth() && curr < start;
        const isNextMonth = curr >= end;
        const className = (isOtherMonth || isNextMonth) ? 'other-month' : '';

        html += `<td class="${className}"><span class="day-number">${curr.getDate()}</span>`;

        // Find events for this day
        const dayEvents = events.filter(e => {
          const eStart = new Date(e.start as any);
          return eStart.toDateString() === curr.toDateString();
        });

        dayEvents.forEach(e => {
          const cat = this.getEventCategory(e);
          const colorClass = cat ? `bg-${cat}` : 'bg-primary';
          html += `<div class="event-item ${colorClass}">${this.escapeHtml(e.title || '')}</div>`;
        });

        html += '</td>';
        curr.setDate(curr.getDate() + 1);
      }
      html += '</tr>';
      if (curr >= end && w >= 3) {
        break;
      } // Optimization: stop if we passed the end and shown at least 4 weeks
    }

    html += '</tbody></table>';
    return html;
  }

  private buildWeekGrid(start: Date, end: Date, events: EventInput[]): string {
    let html = '<table class="calendar-grid"><thead><tr>';
    const curr = new Date(start);
    for (let i = 0; i < 7; i++) {
      const dayName = curr.toLocaleDateString('en-US', {weekday: 'short'});
      html += `<th>${dayName} ${curr.getDate()}</th>`;
      curr.setDate(curr.getDate() + 1);
    }
    html += '</tr></thead><tbody><tr>';

    curr.setTime(start.getTime());
    for (let i = 0; i < 7; i++) {
      html += '<td>';
      const dayEvents = events.filter(e => {
        const eStart = new Date(e.start as any);
        return eStart.toDateString() === curr.toDateString();
      });

      dayEvents.forEach(e => {
        const cat = this.getEventCategory(e);
        const colorClass = cat ? `bg-${cat}` : 'bg-primary';
        const timeStr = e.start ? new Date(e.start as any).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '';
        html += `<div class="event-item ${colorClass}"><strong>${timeStr}</strong> ${this.escapeHtml(e.title || '')}</div>`;
      });

      html += '</td>';
      curr.setDate(curr.getDate() + 1);
    }
    html += '</tr></tbody></table>';
    return html;
  }

  private buildDayGrid(start: Date, end: Date, events: EventInput[]): string {
    // For single day, we use a slightly different vertical layout
    let html = `
      <style>
        .day-view-container { border: 1px solid #e0e0e0; border-radius: 8px; padding: 20px; background: #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
        .day-event-card { margin-bottom: 15px; border-left: 5px solid; padding: 12px 15px; border-radius: 4px; background: #fafafa; border-bottom: 1px solid #eee; }
        .border-primary { border-left-color: #4e73df !important; }
        .border-success { border-left-color: #1cc88a !important; }
        .border-danger { border-left-color: #e74a3b !important; }
        .border-warning { border-left-color: #f6c23e !important; }
        .border-info { border-left-color: #36b9cc !important; }
        .border-secondary { border-left-color: #858796 !important; }
        .border-dark { border-left-color: #5a5c69 !important; }
        .border-default { border-left-color: #4e73df !important; }
      </style>
      <div class="day-view-container">
    `;
    const sorted = [...events].sort((a, b) => new Date(a.start as any).getTime() - new Date(b.start as any).getTime());

    if (sorted.length === 0) {
      html += '<p class="text-center" style="padding: 20px; color: #999; font-style: italic;">No events scheduled for this day.</p>';
    } else {
      sorted.forEach(e => {
        const cat = this.getEventCategory(e);
        const timeStr = e.start ? new Date(e.start as any).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '';
        const endTimeStr = e.end ? ` - ${new Date(e.end as any).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}` : '';
        const desc = this.getEventDescription(e);

        html += `
          <div class="day-event-card border-${cat}">
            <div style="font-weight: 700; font-size: 12px; color: #666; margin-bottom: 4px;">${timeStr}${endTimeStr}</div>
            <div style="font-size: 16px; font-weight: 600; color: #4e73df; margin-bottom: 5px;">${this.escapeHtml(e.title || '')}</div>
            ${desc ? `<div style="font-size: 13px; color: #444; font-style: italic; line-height: 1.4;">${this.escapeHtml(String(desc))}</div>` : ''}
          </div>
        `;
      });
    }
    html += '</div>';
    return html;
  }

  private getEventCategory(event: EventInput): string {
    const rawClassNames: any = (event as any).classNames ?? (event as any).className ?? '';
    const classNamesStr = Array.isArray(rawClassNames) ? rawClassNames.join(' ') : String(rawClassNames || '');
    const derivedCategory = classNamesStr ? classNamesStr.replace(/bg-/g, '').trim() : '';
    return (event as any).Category
      ?? (event as any)?.extendedProps?.Category
      ?? derivedCategory
      ?? 'default';
  }

  private getEventDescription(event: EventInput): string {
    return (event as any)?.extendedProps?.Description
      ?? (event as any)?.extendedProps?.description
      ?? (event as any).Description
      ?? '';
  }

  private escapeHtml(unsafe: string): string {
    return (unsafe || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
